/**
 * Invoices / Bills page — thin orchestrator.
 *
 * All state is delegated to useInvoicesPage + useInvoiceActions.
 * All rendering is delegated to InvoiceListView, InvoiceDetailView,
 * InvoiceFormView, and InvoiceModals.
 */

import { useEffect } from 'react'

import { usePageHeader, useShortcuts, useEscapeNavigation } from '@elsapiens/providers'
import { useSearchParams , Link } from 'react-router-dom'


import { InvoiceDetailView, makePaymentSuccessHandler } from './InvoiceDetailView'
import { InvoiceFormView } from './InvoiceFormView'
import { InvoiceListView } from './InvoiceListView'
import { PostConfirmSimpleModal, VoidModal, PostConfirmModal , WriteOffModal, VersionHistoryModal, PaymentModal } from './InvoiceModals'
import { useInvoiceActions } from './useInvoiceActions'
import { useInvoicesPage } from './useInvoicesPage'
import { useToast } from '../../components/ui'
import { useSellerDetails } from '../../hooks/use-seller-details'

type InvoicesPage = ReturnType<typeof useInvoicesPage>
type InvoiceActions = ReturnType<typeof useInvoiceActions>

function getListLabel(isVendor: boolean, isProforma: boolean): string {
  if (isVendor) {
    return isProforma ? 'Draft Bills' : 'Posted Bills'
  }
  return isProforma ? 'Draft Invoices' : 'Posted Invoices'
}

function detailCrumbLabel(page: InvoicesPage): string {
  if (page.viewMode === 'create') { return page.isVendor ? 'New Bill' : 'New Invoice' }
  if (page.viewMode === 'edit') { return page.selectedInvoice ? `${page.selectedInvoice.invoiceNumber} (Edit)` : 'Edit' }
  return page.selectedInvoice?.invoiceNumber ?? 'Details'
}

function useInvoiceBreadcrumbs(page: InvoicesPage): { listLabel: string } {
  const hubPath = page.isVendor ? '/purchases' : '/sales'
  const hubLabel = page.isVendor ? 'Purchases' : 'Sales'
  const listLabel = getListLabel(page.isVendor, page.isProforma)
  const isDetail = page.viewMode === 'view' || page.viewMode === 'edit' || page.viewMode === 'create'

  // In a detail/edit/create view the list itself becomes a clickable crumb, so
  // Escape (which targets the last crumb with an href) returns to the LIST —
  // preserving its in-memory filters/paging — instead of jumping to the hub.
  const invoiceBreadcrumbs = isDetail
    ? [
        { label: 'Home', href: '/', as: Link },
        { label: hubLabel, href: hubPath, as: Link },
        { label: listLabel, href: page.basePath, as: Link },
        { label: detailCrumbLabel(page) },
      ]
    : [
        { label: 'Home', href: '/', as: Link },
        { label: hubLabel, href: hubPath, as: Link },
        { label: listLabel },
      ]
  usePageHeader({ title: isDetail ? detailCrumbLabel(page) : listLabel, breadcrumbs: invoiceBreadcrumbs })
  useEscapeNavigation(page.navigate, invoiceBreadcrumbs)
  return { listLabel }
}

function useInvoiceShortcuts(page: InvoicesPage, actions: InvoiceActions): void {
  const { register, pushContext, popContext } = useShortcuts()
  useEffect(() => {
    pushContext('invoices')
    return () => popContext('invoices')
  }, [pushContext, popContext])
  useEffect(() => {
    const label = page.isVendor ? 'New Bill' : 'New Invoice'
    const category = page.isVendor ? 'Bills' : 'Invoices'
    const draftPath = page.isVendor ? '/bills/draft' : '/invoices/proforma'
    const postedPath = page.isVendor ? '/bills/final' : '/invoices/final'
    const cleanups = [
      register({
        id: 'invoices-new', keys: 'n', label,
        description: `Create a new ${label.toLowerCase()}`,
        category, scope: 'context', context: 'invoices',
        action: () => { if (page.viewMode === 'list') { actions.handleCreate() } },
      }),
      register({
        id: 'invoices-drafts', keys: 'k',
        label: page.isVendor ? 'View Draft Bills' : 'View Draft Invoices',
        description: `Switch to draft ${page.isVendor ? 'bills' : 'invoices'}`,
        category, scope: 'context', context: 'invoices',
        action: () => { page.navigate(draftPath) },
      }),
      register({
        id: 'invoices-posted', keys: 'l',
        label: page.isVendor ? 'View Final Bills' : 'View Final Invoices',
        description: `Switch to final ${page.isVendor ? 'bills' : 'invoices'}`,
        category, scope: 'context', context: 'invoices',
        action: () => { page.navigate(postedPath) },
      }),
    ]
    return () => cleanups.forEach(fn => fn())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, page.isVendor, page.viewMode])
}

interface SharedModalsProps {
  page: InvoicesPage
  actions: InvoiceActions
  includePostingDate?: boolean
}

function PostAndVoidModals({ page, actions, includePostingDate }: SharedModalsProps): JSX.Element {
  return (
    <>
      {includePostingDate ? (
        <PostConfirmModal
          invoiceToPost={actions.invoiceToPost}
          isLoading={page.isLoading}
          isOpen={actions.isPostConfirmVisible}
          isVendor={page.isVendor}
          postingDate={actions.postingDate}
          onClose={() => actions.setIsPostConfirmVisible(false)}
          onConfirm={() => { void actions.confirmPost() }}
          onPostingDateChange={actions.setPostingDate}
        />
      ) : (
        <PostConfirmSimpleModal
          invoiceToPost={actions.invoiceToPost}
          isLoading={page.isLoading}
          isOpen={actions.isPostConfirmVisible}
          isVendor={page.isVendor}
          onClose={() => actions.setIsPostConfirmVisible(false)}
          onConfirm={() => { void actions.confirmPost() }}
        />
      )}
      <VoidModal
        invoiceToVoid={actions.invoiceToVoid}
        isLoading={page.isLoading}
        isOpen={actions.isVoidModalVisible}
        isVendor={page.isVendor}
        voidReason={actions.voidReason}
        onClose={() => actions.setIsVoidModalVisible(false)}
        onConfirm={() => { void actions.confirmVoid() }}
        onVoidReasonChange={actions.setVoidReason}
      />
    </>
  )
}

interface CreateOrEditViewProps {
  page: InvoicesPage
  actions: InvoiceActions
  addToast: ReturnType<typeof useToast>['addToast']
  searchParams: URLSearchParams
  sellerDetails: ReturnType<typeof useSellerDetails>
}

function CreateOrEditView({ page, actions, addToast, searchParams, sellerDetails }: CreateOrEditViewProps): JSX.Element {
  return (
    <>
      <InvoiceFormView
        actions={actions}
        addToast={addToast}
        basePath={page.basePath}
        cloneSource={page.cloneSource}
        currentLedgerId={page.currentLedgerId}
        isLoading={page.isLoading}
        isLoadingSellerDetails={sellerDetails.isLoading}
        isVendor={page.isVendor}
        navigate={page.navigate}
        organizationId={page.organizationId}
        parties={page.parties}
        searchParams={searchParams}
        selectedInvoice={page.selectedInvoice}
        sellerDetails={sellerDetails.sellerDetails ?? null}
        sellerDetailsError={sellerDetails.error ?? null}
        viewMode={page.viewMode}
        onFetchParties={page.fetchParties}
        onRefetchSellerDetails={sellerDetails.refetch}
      />
      <PostAndVoidModals includePostingDate actions={actions} page={page} />
    </>
  )
}

function computeOutstanding(actions: InvoiceActions, totalAmount: number | string | undefined | null): number {
  const allocations = actions.invoicePayments?.allocations ?? []
  const totalPaid = allocations.reduce((s, a) => s + (Number(a.allocatedAmount) || 0), 0)
  return Number(totalAmount ?? 0) - totalPaid
}

interface InvoiceViewProps {
  page: InvoicesPage
  actions: InvoiceActions
}

interface ExtraInvoiceModalsProps {
  page: InvoicesPage
  actions: InvoiceActions
  inv: NonNullable<InvoicesPage['selectedInvoice']>
}

function ExtraInvoiceModals({ page, actions, inv }: ExtraInvoiceModalsProps): JSX.Element {
  const invoiceCurrency = inv.currency ?? ''
  const outstandingBalance = computeOutstanding(actions, inv.totalAmount)
  return (
    <>
      <WriteOffModal
        invoiceCurrency={invoiceCurrency}
        isOpen={actions.isWriteOffConfirmVisible}
        isWriteOffSaving={actions.isWriteOffSaving}
        outstandingBalance={outstandingBalance}
        writeOffNote={actions.writeOffNote}
        writeOffType={actions.writeOffType}
        onClose={() => actions.setIsWriteOffConfirmVisible(false)}
        onConfirm={() => { void actions.handleWriteOff() }}
        onNoteChange={actions.setWriteOffNote}
      />
      <VersionHistoryModal
        invoiceId={inv.id}
        isOpen={actions.isVersionHistoryVisible}
        orgId={page.organizationId}
        onClose={() => actions.setIsVersionHistoryVisible(false)}
      />
      {page.currentLedgerId && (
        <PaymentModal
          invoice={inv}
          isOpen={actions.isPaymentModalVisible}
          ledgerId={page.currentLedgerId}
          organizationId={page.organizationId}
          onClose={() => actions.setIsPaymentModalVisible(false)}
          onSuccess={makePaymentSuccessHandler(
            actions.setIsPaymentModalVisible,
            page.organizationId,
            inv,
            page.setSelectedInvoice,
            actions.fetchInvoicePayments,
            page.fetchInvoices,
          )}
        />
      )}
    </>
  )
}

function InvoiceDetailWithModals({ page, actions }: InvoiceViewProps): JSX.Element | null {
  const inv = page.selectedInvoice
  if (!inv) {
    return null
  }
  return (
    <>
      <InvoiceDetailView
        actions={actions}
        currentLedgerId={page.currentLedgerId}
        fetchInvoices={page.fetchInvoices}
        invoice={inv}
        isLoading={page.isLoading}
        isProforma={page.isProforma}
        isVendor={page.isVendor}
        organizationId={page.organizationId}
      />
      <PostAndVoidModals includePostingDate actions={actions} page={page} />
      <ExtraInvoiceModals actions={actions} inv={inv} page={page} />
    </>
  )
}

function InvoiceListWithModals({ page, actions }: InvoiceViewProps): JSX.Element {
  return (
    <>
      <InvoiceListView actions={actions} page={page} />
      <PostAndVoidModals actions={actions} page={page} />
    </>
  )
}

export function Invoices(): JSX.Element {
  const page = useInvoicesPage()
  const { addToast } = useToast()
  useInvoiceBreadcrumbs(page)
  const [searchParams] = useSearchParams()

  const actions = useInvoiceActions({
    organizationId: page.organizationId,
    currentLedgerId: page.currentLedgerId,
    isVendor: page.isVendor,
    selectedInvoice: page.selectedInvoice,
    setSelectedInvoice: page.setSelectedInvoice,
    setIsLoading: page.setIsLoading,
    fetchInvoices: page.fetchInvoices,
    navigate: page.navigate,
    basePath: page.basePath,
    viewMode: page.viewMode,
  })

  useInvoiceShortcuts(page, actions)

  // Keep invoicePayments in sync when viewing
  useEffect(() => {
    if (page.viewMode === 'view' && page.selectedInvoice) {
      void actions.fetchInvoicePayments(page.selectedInvoice.id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO(refactor): verify dependency array intentional
  }, [page.viewMode, page.selectedInvoice?.id])

  const sellerDetails = useSellerDetails(page.organizationId, page.currentLedgerId ?? undefined)

  if (page.viewMode === 'create' || page.viewMode === 'edit') {
    return (
      <CreateOrEditView
        actions={actions}
        addToast={addToast}
        page={page}
        searchParams={searchParams}
        sellerDetails={sellerDetails}
      />
    )
  }
  if (page.viewMode === 'view' && page.selectedInvoice) {
    return <InvoiceDetailWithModals actions={actions} page={page} />
  }
  return <InvoiceListWithModals actions={actions} page={page} />
}

export default Invoices
